import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Spinner, SPINNER_MOTION } from '@/components/ui/spinner';
import { useAxios } from '@/hooks/useAxios';
import { useRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import type { Message } from '@/types';
import { cn } from '@/utils/classname';

interface ConstantineProgressPanelProps {
  conversationId: string;
  title: string;
  onComplete?: () => void;
  hideHeader?: boolean;
}

const POLL_INTERVAL_MS = 3_000;

export function ConstantineProgressPanel({
  conversationId,
  title,
  onComplete,
  hideHeader,
}: ConstantineProgressPanelProps) {
  const axios = useAxios();
  // Messages are keyed by conversationId so a conversation switch derives an
  // empty list immediately instead of resetting state in an effect.
  const [messagesState, setMessagesState] = useState<{
    id: string;
    list: Message[];
  }>({ id: conversationId, list: [] });
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  // conversationId that onComplete already fired for
  const completedForRef = useRef<string | null>(null);

  const toggleStep = (key: string) => {
    setExpandedSteps(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Poll for messages (fallback for missed WS events).
  // Follows the useRecorderMessages pattern: polls every 3s, skips state
  // updates when the list is identity-stable to avoid downstream re-renders.
  useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;

    const fetchOnce = async () => {
      try {
        const response = await axios.get('/my', {
          params: { label: 'message', key: `#message#${conversationId}` },
        });
        if (cancelled) return;
        const list = (response.data.messages || []) as Message[];
        setMessagesState(prev =>
          prev.id === conversationId && sameMessages(prev.list, list)
            ? prev
            : { id: conversationId, list }
        );
      } catch {
        // Ignore poll failures — next tick retries
      }
    };

    void fetchOnce();
    const interval = setInterval(fetchOnce, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [conversationId, axios]);

  // WebSocket subscription for real-time nudges. On each WS event we
  // immediately re-fetch rather than waiting for the next poll tick.
  const fetchRef = useRef<(() => void) | undefined>(undefined);
  useEffect(() => {
    fetchRef.current = () => {
      if (!conversationId) return;
      void axios
        .get('/my', {
          params: { label: 'message', key: `#message#${conversationId}` },
        })
        .then(response => {
          const list = (response.data.messages || []) as Message[];
          setMessagesState(prev =>
            prev.id === conversationId && sameMessages(prev.list, list)
              ? prev
              : { id: conversationId, list }
          );
        })
        .catch(() => {});
    };
  });

  const handleWsUpdate = useCallback(() => {
    fetchRef.current?.();
  }, []);

  useRealtimeSubscription({
    patterns: conversationId
      ? [{ pattern: `#message#${conversationId}#`, matchType: 'prefix' }]
      : [],
    enabled: !!conversationId,
    onUpdate: handleWsUpdate,
  });

  // Filter to chariot messages only (progress updates from the backend)
  const steps = useMemo(() => {
    const messages =
      messagesState.id === conversationId ? messagesState.list : [];
    return messages
      .filter(m => m.role === 'chariot' && m.content)
      .sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  }, [messagesState, conversationId]);

  // Derive completion from the message data rather than storing in state.
  // A message starting with "Done" signals the workflow has finished.
  const isComplete = steps.some(s => s.content?.startsWith('Done'));

  // Fire onComplete exactly once per conversation when completion is detected.
  useEffect(() => {
    if (!isComplete || completedForRef.current === conversationId) return;
    completedForRef.current = conversationId;
    onComplete?.();
  }, [isComplete, conversationId, onComplete]);

  // Auto-scroll the inner list so the latest step is always visible
  const listRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [steps.length]);

  // Extract PR URL from messages (search backwards for the most recent one)
  const prUrl = useMemo(() => {
    for (let i = steps.length - 1; i >= 0; i--) {
      const match = steps[i].content?.match(
        /https:\/\/github\.com\/[^\s)]+\/pull\/\d+/
      );
      if (match) return match[0];
    }
    return null;
  }, [steps]);

  return (
    <div
      className={cn(
        'overflow-hidden',
        !hideHeader && 'border-chariot-stroke rounded-lg border'
      )}
    >
      {!hideHeader && (
        <div className="bg-chariot-background-secondary flex items-center justify-between px-4 py-3">
          <h5 className="text-chariot-text-primary text-sm font-medium">
            {title}
          </h5>
          {!isComplete && <Spinner className="size-4" />}
        </div>
      )}

      {steps.length === 0 ? (
        <div className="text-chariot-text-secondary flex items-center gap-2 p-3 text-sm">
          <Spinner />
          Waiting for Constantine to start...
        </div>
      ) : (
        <ul
          ref={listRef}
          className="border-chariot-stroke ml-5 max-h-[40vh] overflow-y-auto border-l"
        >
          {steps.map((step, i) => {
            const isLast = i === steps.length - 1;
            const status = isLast && !isComplete ? 'running' : 'success';
            const isExpanded = expandedSteps.has(step.key);
            const [stepTitle, stepResponse] = (step.content ?? '').split(
              '\n---RESPONSE---\n'
            );
            const hasResponse = !!stepResponse;
            return (
              <li key={step.key} className="relative">
                <button
                  type="button"
                  onClick={hasResponse ? () => toggleStep(step.key) : undefined}
                  className={cn(
                    'flex w-full items-start gap-2 py-2 pr-3 pl-4 text-left',
                    hasResponse &&
                      'hover:bg-chariot-background-secondary/30 cursor-pointer'
                  )}
                >
                  {/* Horizontal branch from tree line */}
                  <span className="text-chariot-text-secondary border-chariot-stroke absolute top-3.5 -left-px w-3 border-t" />

                  {hasResponse && (
                    <ChevronRightIcon
                      className={cn(
                        'text-chariot-text-secondary mt-1 size-3 shrink-0 transition-transform',
                        isExpanded && 'rotate-90'
                      )}
                    />
                  )}
                  <StepIcon status={status} />
                  <span className="text-chariot-text-primary min-w-0 flex-1 text-sm font-medium">
                    {stepTitle}
                  </span>
                </button>
                {isExpanded && stepResponse && (
                  <div className="border-chariot-stroke bg-chariot-background-secondary/40 mr-3 mb-1 ml-7 max-h-[200px] overflow-y-auto rounded-r border-l-2 p-2.5">
                    <pre className="text-chariot-text-secondary font-mono text-xs leading-relaxed break-all whitespace-pre-wrap">
                      {stepResponse}
                    </pre>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {prUrl && (
        <div className="border-chariot-stroke border-t px-4 py-3">
          <a
            href={prUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-chariot-stroke-blue flex items-center gap-1 text-xs font-medium hover:opacity-80"
          >
            <ArrowTopRightOnSquareIcon className="size-4" />
            View Pull Request
          </a>
        </div>
      )}
    </div>
  );
}

function StepIcon({ status }: { status: 'running' | 'success' | 'error' }) {
  switch (status) {
    case 'running':
      return (
        <ArrowPathIcon
          className={cn(
            'text-chariot-brand-primary size-5 shrink-0',
            SPINNER_MOTION
          )}
        />
      );
    case 'success':
      return (
        <CheckCircleIcon className="text-chariot-status-green size-5 shrink-0" />
      );
    case 'error':
      return (
        <XCircleIcon className="text-chariot-status-red size-5 shrink-0" />
      );
  }
}

// Stable-identity check: skip the state update when the polled list matches
// what we already have. Same pattern as useRecorderMessages.
function sameMessages(a: Message[], b: Message[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].key !== b[i].key || a[i].content !== b[i].content) return false;
  }
  return true;
}
